import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Star, Check, Zap } from 'lucide-react';
import ChestIcon from './ChestIcon';
import { Prism, getPrismDescription, getPotentialDescription } from '../utils/prismSystem';
import { cn } from '../lib/utils';
import { audioEngine } from '../utils/audioEngine';
import Prism3D from './Prism3D';
import Confetti from './Confetti';

interface PrismUnboxingModalProps {
  prism: Prism;
  onClose: () => void;
}


export default function PrismUnboxingModal({ prism, onClose }: PrismUnboxingModalProps) {
  const [step, setStep] = useState<'shaking' | 'revealing' | 'done'>('shaking');

  useEffect(() => {
    // Sequence
    const t1 = setTimeout(() => setStep('revealing'), 1500);
    const t2 = setTimeout(() => {
      setStep('done');
      audioEngine.playSound('success'); 
    }, 2500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [step]);

  const colorClasses: Record<string, string> = {
    red: "from-rose-400 to-rose-600 shadow-rose-200",
    yellow: "from-amber-300 to-amber-500 shadow-amber-200",
    green: "from-emerald-400 to-emerald-600 shadow-emerald-200",
    blue: "from-cyan-400 to-cyan-600 shadow-cyan-200",
    violet: "from-violet-400 to-violet-600 shadow-violet-200"
  };

  return (
    <div className="absolute inset-0 z-[600] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-md">
      <Confetti active={step === 'done'} count={prism.stars * 50} />
      <AnimatePresence mode="wait">
        {step === 'shaking' && (
          <motion.div
            key="shaking"
            initial={{ scale: 0, rotate: -20 }}
            animate={{ 
              scale: 1, 
              rotate: 0,
              x: [0, -5, 5, -5, 5, 0],
              y: [0, 5, -5, 5, -5, 0]
            }}
            transition={{ 
              scale: { type: "spring", stiffness: 200 },
              x: { repeat: Infinity, duration: 0.1 },
              y: { repeat: Infinity, duration: 0.1 }
            }}
            className="w-48 h-48 bg-white/10 rounded-[48px] shadow-2xl flex items-center justify-center border-4 border-white/20 overflow-hidden"
          >
            <ChestIcon tier="gold" size="xl" />
          </motion.div>
        )}

        {(step === 'revealing' || step === 'done') && (
          <motion.div
            key="revealed"
            initial={{ scale: 0.3, opacity: 0, rotate: -30, y: 100 }}
            animate={{ scale: 1, opacity: 1, rotate: 0, y: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 180, 
              damping: 13,
              mass: 0.8
            }}
            className="w-full max-w-sm flex flex-col items-center"
          >
            {/* Info */}
            <div className="flex gap-1 mb-4 h-4">
              {Array.from({ length: prism.stars }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
              ))}
            </div>

            {/* The Prism itself */}
            <motion.div
              animate={{ 
                y: [0, -15, 0],
              }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="w-64 h-64 flex items-center justify-center relative mb-8"
            >
               <div className={cn(
                 "absolute inset-0 rounded-full blur-3xl opacity-20 bg-current",
                 {
                   red: "text-rose-500", yellow: "text-amber-500", green: "text-emerald-500", blue: "text-cyan-500", violet: "text-violet-500"
                 }[prism.color || 'blue']
               )} />
              <motion.div
                animate={prism.stars >= 4 ? {
                  x: [0, -2, 2, -2, 2, 0],
                  y: [0, 2, -2, 2, -2, 0]
                } : {}}
                transition={{
                  repeat: Infinity,
                  duration: 0.1,
                  repeatType: "reverse"
                }}
              >
                <Prism3D color={prism.color} stars={prism.stars} isCorrupted={!!prism.blackStats} potentials={[prism.slot1, prism.slot2, prism.slot3].filter(Boolean) as any} />
              </motion.div>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ 
                type: "spring", 
                stiffness: 150, 
                damping: 14, 
                delay: 0.1 
              }}
              className="text-center w-full"
            >
              <h2 className="text-4xl font-num text-white uppercase mb-1 tracking-tight">
                {prism.stars}-Star {prism.color}
              </h2>
              <p className="text-cyan-400 font-black uppercase tracking-[0.2em] text-[10px] mb-8">
                {getPrismDescription(prism)}
              </p>

              {/* Potentials List */}
              <div className="space-y-3 w-full mb-10">
                {[prism.slot1, prism.slot2, prism.slot3].filter(Boolean).map((pot, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -30, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 180, 
                      damping: 14, 
                      delay: 0.15 + i * 0.08 
                    }}
                    className="bg-white/10 backdrop-blur-md p-4 rounded-3xl border border-white/10 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-cyan-400/20 flex items-center justify-center">
                         <Zap className="w-4 h-4 text-cyan-400" />
                      </div>
                      <span className="text-sm font-black text-white uppercase tracking-tight">
                        {getPotentialDescription(pot!)}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {step === 'done' && (
                <button
                  onClick={onClose}
                  className="w-full bg-white text-slate-950 font-num text-2xl py-6 rounded-3xl toy-3d-button border-none"
                >
                  COLLECT ITEM
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
